import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'hero' | 'interactive' | 'static';

const variants: Record<Variant, string> = {
  hero: [
    'rounded-[var(--radius-card)] p-5',
    'bg-[var(--glass-bg)] backdrop-blur-[12px]',
    'border border-[var(--glass-border)]',
    'shadow-[0_1px_3px_oklch(0.15_0.01_250/0.06),0_4px_12px_oklch(0.15_0.01_250/0.04)]',
  ].join(' '),
  interactive: [
    'rounded-[var(--radius-card)] border border-hairline bg-surface p-5',
    'shadow-[var(--shadow-card)]',
    'transition-[box-shadow,transform] duration-200 ease-out',
    'hover:shadow-[var(--shadow-hover)] hover:-translate-y-px',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
    'cursor-pointer',
  ].join(' '),
  static: 'rounded-[var(--radius-card)] border border-hairline bg-surface p-5 shadow-[var(--shadow-card)]',
};

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
}

export function Card({ variant = 'static', className = '', ...props }: CardProps) {
  const interactiveProps = variant === 'interactive' ? { tabIndex: 0 } : {};
  return (
    <div className={cn(variants[variant], className)} {...interactiveProps} {...props} />
  );
}

/** Metric block: uppercase label + mono figure. */
export function Metric({
  label,
  value,
  sub,
  tone = 'ink',
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  tone?: 'ink' | 'green-fg' | 'amber-fg';
}) {
  return (
    <div className="p-[17px_18px]">
      <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-label">
        {label}
      </p>
      <p className={cn('m-0 font-mono text-[26px] font-semibold tracking-[-0.02em]', `text-${tone}`)}>
        {value}
      </p>
      {sub && <p className="m-0 mt-1.5 text-[11.5px] text-muted-label">{sub}</p>}
    </div>
  );
}
